import { useMemo } from 'react';
import dayjs from 'dayjs';

const MONTH_LABELS = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
];

/**
 * Obtiene la clave mes-año (YYYY-MM) de una nota usando lastUpdated o createdAt.
 */
const getNoteMonthKey = (note) => {
  const dateStr = note?.lastUpdated ?? note?.createdAt;
  if (!dateStr) return null;
  const d = dayjs(dateStr);
  return d.isValid() ? d.format('YYYY-MM') : null;
};

const isArchived = (n) => String(n?.status || '').toUpperCase() === 'ARCHIVED';

/**
 * Agrupa notas: pinned arriba, grupos por mes en el medio, archived al final.
 * @param {Array} notes - Lista de notas (ya ordenada: pinned, resto, archived)
 * @returns {Array<{ key: string, label: string|null, notes: Array }>}
 */
export const useNotesGroupedByMonth = (notes = []) => {
  return useMemo(() => {
    const pinned = [];
    const archived = [];
    const middle = [];
    for (const n of notes) {
      if (Boolean(n.isPinned)) pinned.push(n);
      else if (isArchived(n)) archived.push(n);
      else middle.push(n);
    }

    const currentMonthKey = dayjs().format('YYYY-MM');
    const byMonth = new Map();

    for (const note of middle) {
      const key = getNoteMonthKey(note) || currentMonthKey;
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key).push(note);
    }

    const sortedKeys = Array.from(byMonth.keys()).sort((a, b) => b.localeCompare(a));
    const monthGroups = sortedKeys.map((key) => {
      const [year, month] = key.split('-');
      const monthIndex = parseInt(month, 10) - 1;
      const label =
        key === currentMonthKey
          ? null
          : `${MONTH_LABELS[monthIndex] || month} ${year}`;

      return {
        key,
        label,
        notes: byMonth.get(key),
      };
    });

    const groups = [...monthGroups];
    if (pinned.length > 0) groups.unshift({ key: 'pinned', label: null, notes: pinned });
    if (archived.length > 0) groups.push({ key: 'archived', label: null, notes: archived });
    return groups;
  }, [notes]);
};

export default useNotesGroupedByMonth;
